import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import {
  Alert,
  Button,
  Card,
  Heading,
  Input,
} from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const {
    login,
    loginWithGoogle,
    oauthLoading,
    oauthError,
    submitTotpChallenge,
    startTotpEnrollment,
    verifyTotpEnrollment,
    clearPendingTotp,
  } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [devLoginPending, setDevLoginPending] = useState<string | null>(null);
  // GH #1916: inline TOTP step machine. 'credentials' renders the password
  // form; 'challenge' the 6-digit / recovery-code entry; 'enroll' the QR +
  // first-code verify; 'recovery-codes' the one-time code display after a
  // fresh enrollment.
  const [step, setStep] = useState<'credentials' | 'challenge' | 'enroll' | 'recovery-codes'>('credentials');
  const [totpCode, setTotpCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryInput, setRecoveryInput] = useState('');
  const [enrollment, setEnrollment] = useState<{ secret: string; qrCodeDataUrl: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  // Pick up error from URL query params (OAuth error redirect)
  const urlError = new URLSearchParams(window.location.search).get('error');
  const urlMessage = new URLSearchParams(window.location.search).get('message');
  const displayError = error ?? oauthError ?? (urlError ? (urlMessage ?? 'Authentication failed') : null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await login(email, password);
      if (result.totpEnrollmentRequired) {
        const enrolled = await startTotpEnrollment();
        setEnrollment({ secret: enrolled.secret, qrCodeDataUrl: enrolled.qrCodeDataUrl });
        setStep('enroll');
      } else if (result.totpRequired) {
        setStep('challenge');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChallengeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const result = useRecovery
        ? await submitTotpChallenge({ recoveryCode: recoveryInput.trim() })
        : await submitTotpChallenge({ code: totpCode.trim() });
      if (result.recoveryCodesRemaining !== undefined && result.recoveryCodesRemaining <= 2) {
        // Surface the running-low warning on the next screen via the URL-free
        // path: the operator lands authenticated; low-code guidance belongs in
        // the security settings surface. Log only.
        console.warn(`TOTP recovery codes remaining: ${result.recoveryCodesRemaining}`);
      }
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnrollVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const { recoveryCodes: codes } = await verifyTotpEnrollment(totpCode.trim());
      setRecoveryCodes(codes);
      setTotpCode('');
      setStep('recovery-codes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrollment verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // After the operator confirms they saved the recovery codes: re-login with
  // the credentials still in state to obtain a challenge-scoped intermediate,
  // then ask for the current authenticator code (the enrollment intermediate
  // is spent by design -- the fresh challenge proves possession end-to-end).
  const handleRecoveryCodesSaved = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      setRecoveryCodes([]);
      const result = await login(email, password);
      if (result.totpRequired) {
        setStep('challenge');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
      setStep('credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  const backToCredentials = () => {
    clearPendingTotp();
    setStep('credentials');
    setTotpCode('');
    setRecoveryInput('');
    setUseRecovery(false);
    setEnrollment(null);
    setError(null);
  };

  // Sprint D Lane D1 (2026-05-17, F-XPORTAL-MOCK-JWT-001 closure):
  // The prior dev quick-login buttons synthesised mock User records and
  // `demo-token-${accountType}` access tokens via `useAuthStore.setState`.
  // Those tokens were rejected by every peer service that validates JWT
  // signatures (LSP / CRT / platform-admin-api proxy targets), surfacing as
  // "Invalid token" cascades on Loan Participations surfaces. The buttons now
  // fill the email field with the canonical dev fixture seeded by
  // `staging/aaim-core-new/packages/core/prisma/seed-e2e-fixture.ts`; the
  // operator types the password (read from
  // `~/Documents/dev/aaim/.secrets/<fixture>-password.txt`) and submits the
  // standard form, which calls the real `POST /api/v1/auth/login` and mints
  // a real JWT. Pre-customer atomic transition per
  // `.claude/rules/no-pre-customer-tech-debt.md`: NO mock-token fallback.
  const devFixtureEmails = {
    platformAdmin: 'e2etesting@aaim.com',
    fiAdmin: 'fiadmin@aaim.com',
    loanOfficer: 'loanofficer@aaim.com',
    compliance: 'compliance@aaim.com',
  } as const;

  const handleDevFixturePrefill = (accountType: keyof typeof devFixtureEmails) => {
    const fixtureEmail = devFixtureEmails[accountType];
    setEmail(fixtureEmail);
    setPassword('');
    setError(null);
    setDevLoginPending(fixtureEmail);
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card with red top accent */}
        <Card className="overflow-hidden">
          <div className="h-1 bg-accent" />
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="https://cdn.aaim.app/logos/aaim-master-grey.svg"
                alt="Aaim"
                className="h-8"
              />
            </div>

            {/* Welcome text */}
            <Heading level={1} size="subsection" className="text-center mb-1">
              Welcome back
            </Heading>
            <p className="text-sm font-sans text-tertiary text-center mb-8">
              Sign in to your admin portal
            </p>

            {/* Error */}
            {displayError && (
              <Alert type="error" className="mb-6">
                {displayError}
              </Alert>
            )}

            {/* GH #1916: TOTP challenge step -- 6-digit code or a single-use
                recovery code (device loss). */}
            {step === 'challenge' && (
              <form onSubmit={handleChallengeSubmit}>
                <p className="text-sm text-secondary mb-4">
                  Enter the 6-digit code from your authenticator app for{' '}
                  <span className="font-medium text-primary">{email}</span>.
                </p>
                {!useRecovery ? (
                  <div className="mb-4">
                    <Input
                      label="Authenticator code"
                      id="totp-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      placeholder="123456"
                      required
                    />
                  </div>
                ) : (
                  <div className="mb-4">
                    <Input
                      label="Recovery code"
                      id="recovery-code"
                      type="text"
                      autoComplete="off"
                      value={recoveryInput}
                      onChange={(e) => setRecoveryInput(e.target.value)}
                      placeholder="xxxx-xxxx-xxxx"
                      required
                    />
                    <p className="text-label-sm text-quaternary mt-1">
                      Each recovery code works once.
                    </p>
                  </div>
                )}
                <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Verifying...' : 'Verify'}
                </Button>
                <div className="flex items-center justify-between mt-4">
                  <button
                    type="button"
                    onClick={() => { setUseRecovery(!useRecovery); setError(null); }}
                    className="text-sm text-accent hover:text-accent/80 font-medium"
                  >
                    {useRecovery ? 'Use authenticator code' : 'Lost your device? Use a recovery code'}
                  </button>
                  <button
                    type="button"
                    onClick={backToCredentials}
                    className="text-sm text-tertiary hover:text-secondary"
                  >
                    Back
                  </button>
                </div>
              </form>
            )}

            {/* GH #1916: TOTP enrollment step -- QR + first-code verify. */}
            {step === 'enroll' && enrollment && (
              <form onSubmit={handleEnrollVerify}>
                <p className="text-sm text-secondary mb-4">
                  Two-factor authentication is required. Scan the QR code with
                  your authenticator app, then enter the first 6-digit code.
                </p>
                <div className="flex justify-center mb-4">
                  <img
                    src={enrollment.qrCodeDataUrl}
                    alt="TOTP enrollment QR code"
                    width={180}
                    height={180}
                  />
                </div>
                <p className="text-label-sm text-quaternary mb-4 text-center font-mono break-all">
                  {enrollment.secret}
                </p>
                <div className="mb-4">
                  <Input
                    label="First authenticator code"
                    id="enroll-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="123456"
                    required
                  />
                </div>
                <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Verifying...' : 'Complete enrollment'}
                </Button>
                <button
                  type="button"
                  onClick={backToCredentials}
                  className="text-sm text-tertiary hover:text-secondary mt-4"
                >
                  Back
                </button>
              </form>
            )}

            {/* GH #1916: one-time recovery-code display after enrollment.
                The codes are never shown again and never stored client-side. */}
            {step === 'recovery-codes' && (
              <div>
                <p className="text-sm text-secondary mb-4">
                  Save these single-use recovery codes somewhere safe. Each
                  signs you in once if you lose your authenticator. They are
                  shown only this once.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-6 p-4 bg-recessed rounded font-mono text-sm text-primary">
                  {recoveryCodes.map((code) => (
                    <span key={code}>{code}</span>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  disabled={isSubmitting}
                  onClick={() => { void handleRecoveryCodesSaved(); }}
                >
                  {isSubmitting ? 'Continuing...' : 'I saved my recovery codes -- continue'}
                </Button>
              </div>
            )}

            {step === 'credentials' && (<>
            {/* Login form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <Input
                  label="Email address"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@institution.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="password"
                  className="block text-label-xs font-sans font-semibold uppercase text-tertiary mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-secondary transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Remember me + password-recovery guidance.
                  GH #2307 deliverable 3: this row previously carried a
                  "Forgot password?" button with no onClick -- a dead control
                  on the credential login path, the worst place for one. There
                  is no self-service reset endpoint on this platform (verified:
                  zero reset/forgot/recover routes in
                  staging/aaim-core-new/services/authorization/src/routes/), and
                  the recovery story being built in this arc is deliberately
                  FI-admin-initiated: an administrator with staff:reset_credential
                  issues a step-up-gated, audited reset that emails the staff
                  member a single-use link. So the truthful treatment is to name
                  that path rather than to imply a self-service form exists. */}
              <div className="flex items-center justify-between gap-4 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 border-recessed rounded text-accent focus:ring-accent"
                  />
                  <span className="text-sm text-secondary">Remember me</span>
                </label>
                <span className="text-sm text-tertiary text-right">
                  Forgot your password? Ask an administrator at your institution
                  to reset it.
                </span>
              </div>

              {/* Sign in button */}
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            {/* OAuth divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-recessed" />
              <span className="px-4 text-quaternary text-label-xs font-semibold uppercase">
                Or continue with
              </span>
              <div className="flex-1 border-t border-recessed" />
            </div>

            {/* OAuth buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => { loginWithGoogle().catch(() => {}); }}
                disabled={oauthLoading}
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-recessed rounded bg-surface hover:bg-elevated transition-colors text-sm font-medium text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading ? (
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    {/* design-token-skip: Google brand mark (exact brand fills required) */}
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                {oauthLoading ? 'Redirecting...' : 'Google'}
              </button>
              <button
                type="button"
                disabled
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-recessed rounded bg-surface text-sm font-medium text-quaternary cursor-not-allowed"
                title="Microsoft SSO coming soon"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z" />
                </svg>
                Microsoft
              </button>
            </div>
            </>)}

            {/* Security footer */}
            <div className="flex items-center justify-center gap-1.5 mt-6 pt-4 border-t border-recessed">
              <Lock size={12} className="text-quaternary" />
              <span className="text-label-sm text-quaternary">
                Secured with 256-bit encryption
              </span>
            </div>
          </div>
        </Card>

        {/* Below-card footer */}
        <div className="text-center mt-4">
          {/* GH #1962: this was a <button> with no onClick -- a dead control that
              looked clickable and did nothing. Pre-authentication there is no
              in-app case to file, and Aaim does not create FI accounts: an
              administrator at your institution invites you. So say that, as text. */}
          <p className="text-sm text-tertiary">
            Don&apos;t have an account? An administrator at your institution
            creates it and sends the invitation.
          </p>
          <p className="text-xs text-quaternary mt-2">
            Having trouble signing in?{' '}
            <button type="button" className="text-accent/70 hover:text-accent">
              Get help
            </button>
          </p>
        </div>

        {/* Dev-only fixture quick-fill (real auth) */}
        {import.meta.env.DEV && (
          <div className="mt-6">
            <div className="flex items-center my-3">
              <div className="flex-1 border-t border-dashed border-recessed" />
              <span className="px-3 text-quaternary text-label-xs font-semibold uppercase">
                Development accounts
              </span>
              <div className="flex-1 border-t border-dashed border-recessed" />
            </div>
            {devLoginPending && (
              <div className="mb-3 p-3 bg-recessed/50 border border-recessed rounded-sm text-secondary text-xs">
                Email prefilled with {devLoginPending}. Enter the password from
                {' '}<code>~/Documents/dev/aaim/.secrets/</code> and click Sign in.
              </div>
            )}
            <div className="space-y-1.5">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => handleDevFixturePrefill('platformAdmin')}
              >
                Platform Admin (e2etesting@aaim.com)
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => handleDevFixturePrefill('fiAdmin')}
              >
                FI Admin (fiadmin@aaim.com)
              </Button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                <Button
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => handleDevFixturePrefill('loanOfficer')}
                >
                  Loan Officer
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => handleDevFixturePrefill('compliance')}
                >
                  Compliance
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
